/**
 * The auth shell: the split canvas every auth screen sits in — brand lockup, the editorial form
 * column, and the poster pane beside it.
 *
 * Rebuilt to the owner-approved D3 mockup on 21 September 2026
 * (`docs/design/rejestracja-premium-2026-09-21/d3-plakat-dark-losowanie.html`). What changed, and
 * why each of them is the shell's business rather than a screen's:
 *
 * · **The photograph is a POSTER, not a wallpaper.** It sits in a frame inset 40px from the pane's
 *   edges, on the `--photo` plate, and the tag and the hero line stand BELOW it on that plate. The
 *   scrim no longer has to fight for white type over a bright window, because no type is on the
 *   picture any more.
 * · **The head runs on a wider measure than the controls**, so the title and the subtitle set on a
 *   longer line than the fields under them. The whole column is centred in its pane (owner
 *   decision, 21.09.2026) — see the stylesheet's `.form`.
 * · **One orchestrated entrance per step, through Motion.** The `form-in` keyframe this replaced
 *   moved the whole column as one block and could not carry a direction; the cascade below is the
 *   mockup's `rise`, and the direction it travels on the X axis is what tells a trainer whether
 *   they advanced or came back.
 * · **A 2px progress hairline across the top of the page**, on the flow screens only — see
 *   `AuthProgress`.
 *
 * Its interface is still props and no state worth the name: the split, the measures, the brand
 * lockup, the frame, the grade, the headline's accent split and the collapse to one column on a
 * narrow window are all the shell's. A screen inside it decides none of that.
 *
 * The `footer` slot sits at the bottom of the form pane, outside the vertically-centred body,
 * exactly as the mockup draws it. Screen 5's centred "Zły e-mail? Zmień" is NOT this slot — that
 * line lives inside the body with the rest of the confirmation copy, so it stays the screen's own
 * child.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { motion } from 'motion/react';

import { authStepEntrance, screenMotion, useScreenMotionPreference } from '../design-system/motion';
import {
  AUTH_HERO_STEPS,
  type AuthHeroPhoto,
  type AuthHeroStep,
} from '../lib/authHeroJourney';
import { splitAccentedHeadline } from '../lib/heroHeadline';
import { useAuthHeroPhoto } from '../lib/useAuthHeroPhoto';

import brand from './brandMark.module.css';
import styles from './AuthShell.module.css';
import { AuthProgress } from './AuthProgress';
import { HeroPhoto } from './HeroPhoto';

/**
 * **Where the progress hairline last stood, across the remount every step costs.**
 *
 * Every auth step mounts its own `AuthShell` — the steps are different component types, so React
 * swaps rather than reuses — and a component mounted fresh has no idea what the page was showing a
 * moment ago. So the memory is MODULE level, exactly as `HeroPhoto`'s is and for the same reason:
 * the page is ONE physical surface across the flow even though the React tree under it is rebuilt
 * at every step, and this variable is that fact written down.
 *
 * It answers two questions at once, which is why there is one of it rather than two: where the bar
 * grows FROM, and which way the entrance travels. `null` means the visitor has not been on a flow
 * screen in this document — a first arrival, or a screen with no progress at all.
 */
let lastProgress: number | null = null;

interface AuthShellProps {
  /**
   * Anything that sits ABOVE the title — the glyph tile on the confirmation and mobile-app screens.
   * A slot rather than a prop because it is not the shell's own furniture; the shell only owns
   * where that band sits.
   */
  lead?: ReactNode;
  /**
   * The screen's `h1`. Required: every auth screen is a page in its own right and a page without a
   * heading is unnavigable by anything that lists headings.
   */
  title: string;
  /** One line under the title. Optional — the refusal screen leads with a tile instead. */
  subtitle?: string;
  /** Tracked micro-label above the hero headline, on the plate under the frame. */
  heroTag: string;
  /** The hero line. Its final clause carries the brand gradient — see `shared/lib/heroHeadline.ts`. */
  heroHeadline: string;
  /** The mockup's `.foot` line at the bottom of the pane. Usually an `AuthFootnote`. */
  footer?: ReactNode;
  /**
   * Which photograph the frame holds. Sign-up and `/complete-profile` pass the one their own beat
   * rolled (`shared/lib/useAuthHeroPhoto`); omitted, the shell rolls beat ONE for itself — see the
   * hook call in the body. A change between two mounts cross-fades — see `HeroPhoto`.
   */
  photo?: AuthHeroPhoto;
  /**
   * Which beat of the registration flow this screen is, 1 to 4, or omitted for a screen that is not
   * part of one.
   *
   * It draws the hairline and it decides which way the entrance travels. Omitted means neither:
   * sign-in, the password reset and the refusal screen have no progress to show and nowhere to have
   * come from.
   */
  progress?: AuthHeroStep;
  /**
   * Which of the two measures this screen's own content stands on.
   *
   * `'head'` is the role step and nothing else: the mockup draws its two cards on the head's full
   * width, because choosing a role is the whole of that step. Omitted is the controls' measure,
   * which every other auth screen takes. Both numbers live in the shell's stylesheet.
   */
  measure?: 'fields' | 'head';
  children: ReactNode;
}

export function AuthShell({
  lead,
  title,
  subtitle,
  heroTag,
  heroHeadline,
  footer,
  photo: chosenPhoto,
  progress,
  measure,
  children,
}: AuthShellProps) {
  // **A surface that chooses no photograph shows the ARRIVAL** — the same pair, resolved through
  // the same per-visit roll, as registration's first step. Owner decision, 21 September 2026: a
  // trainer who reads sign-in and presses „Załóż konto" meets the person who was already walking in
  // rather than a swap, and the sad landscape hero that sat badly in the portrait frame is gone.
  //
  // Called unconditionally and then discarded where a step passed its own, because that is the
  // rule for hooks. It costs nothing to discard: the roll is the tab's, written once and read back
  // by every later call, so this cannot roll a second, disagreeing set of pictures.
  const arrival = useAuthHeroPhoto(1);
  const photo = chosenPhoto ?? arrival;
  const { lead: headlineLead, accent } = splitAccentedHeadline(heroHeadline);
  const reducedMotion = useScreenMotionPreference();

  // Read ONCE, in a lazy initialiser: the value is about the moment this screen arrived, and a read
  // in the render body would answer differently after the effect below has run.
  const [cameFrom] = useState<number | null>(() => lastProgress);
  const standsAt = progress === undefined ? null : progress / AUTH_HERO_STEPS.length;
  useEffect(() => {
    lastProgress = standsAt;
  }, [standsAt]);

  const direction = standsAt !== null && cameFrom !== null && standsAt < cameFrom ? 'back' : 'forward';
  const entrance = (index: number) => authStepEntrance({ index, direction, reducedMotion });
  const caption = screenMotion('capRise', { reducedMotion });

  return (
    <div className={styles.shell}>
      {standsAt === null ? null : <AuthProgress from={cameFrom ?? 0} to={standsAt} />}

      <div className={styles.pane}>
        {/* "MoveBuddy" is a brand name, not copy — a brand name is not translated, so it is
            deliberately not routed through t(). The mark beside it is decorative: the wordmark is
            right there and says the same thing, so announcing it again would be noise. */}
        <div className={styles.brand}>
          <span className={`${brand.brandMark} ${styles.mark}`} aria-hidden="true" />
          MoveBuddy
        </div>

        <main className={styles.body}>
          <div className={styles.form}>
            <motion.div className={styles.head} {...entrance(0)}>
              {lead}
              <h1 className={styles.title}>{title}</h1>
            </motion.div>

            {subtitle === undefined ? null : (
              <motion.p className={styles.subtitle} {...entrance(1)}>
                {subtitle}
              </motion.p>
            )}

            <motion.div
              className={measure === 'head' ? styles.wide : styles.fields}
              {...entrance(2)}
            >
              {children}
            </motion.div>
          </div>
        </main>

        {footer === undefined ? null : (
          <motion.div className={styles.footer} {...entrance(3)}>
            {footer}
          </motion.div>
        )}
      </div>

      {/* Decorative: the tag and the headline are brand copy, not information the trainer needs to
          complete the form, and the pane is hidden entirely on a narrow window. */}
      <div className={styles.photo} aria-hidden="true">
        <div className={styles.frame}>
          {/* Keyed on the file, so a different photograph is always a fresh mount — which is what
              lets `HeroPhoto` read the outgoing frame at the moment the change happens. */}
          <HeroPhoto key={photo.src} photo={photo} />
        </div>

        <motion.div className={styles.caption} {...caption}>
          <div className={styles.captionTag}>{heroTag}</div>
          <div className={styles.captionLine}>
            {headlineLead}
            <span className={styles.captionAccent}>{accent}</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
